import type { MidiNote } from "./music";

export type MidiEditMode = "select" | "draw" | "voice";
export type TimeSignature = { numerator: number; denominator: number };
export type MidiEditHistory = { undo: MidiNote[][]; redo: MidiNote[][] };
export type TimeNudgeTarget = {
  selectedNoteIndex: number | null;
  barRange: { startBar: number; endBar: number } | null;
  beatsPerBar: number;
};
export type TimeNudgeResult = { notes: MidiNote[]; changedCount: number; appliedDeltaBeats: number };

export const DEFAULT_DRAW_VELOCITY = 92;
export const DEFAULT_TIME_SIGNATURE: TimeSignature = { numerator: 4, denominator: 4 };

const cloneNotes = (notes: MidiNote[]) => notes.map((note) => ({ ...note }));
const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));

export function snapBeat(value: number, gridStep: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(gridStep) || gridStep <= 0) return 0;
  return Math.round(value / gridStep) * gridStep;
}

export function pitchFromRollPosition(y: number, height: number, minimumMidi: number, maximumMidi: number): number {
  const span = Math.max(1, maximumMidi - minimumMidi + 1);
  const ratio = clamp(y / Math.max(1, height), 0, 0.999999);
  return clamp(maximumMidi - Math.floor(ratio * span), minimumMidi, maximumMidi);
}

export function beatFromRollPosition(x: number, width: number, totalBeats: number, gridStep: number): number {
  const maximumStart = Math.max(0, totalBeats - gridStep);
  const rawBeat = clamp(x / Math.max(1, width), 0, 1) * totalBeats;
  return clamp(snapBeat(rawBeat, gridStep), 0, maximumStart);
}

export function createNoteFromRollPosition(input: {
  x: number; y: number; width: number; height: number; totalBeats: number; gridStep: number;
  minimumMidi: number; maximumMidi: number; defaultDurationBeats?: number; velocity?: number;
}): MidiNote {
  return {
    midi: pitchFromRollPosition(input.y, input.height, input.minimumMidi, input.maximumMidi),
    startBeat: beatFromRollPosition(input.x, input.width, input.totalBeats, input.gridStep),
    durationBeats: Math.max(input.gridStep, input.defaultDurationBeats ?? input.gridStep),
    velocity: clamp(Math.round(input.velocity ?? DEFAULT_DRAW_VELOCITY), 1, 127),
  };
}

export function addEditableNote(notes: MidiNote[], added: MidiNote[]): MidiNote[] {
  return [...cloneNotes(notes), ...cloneNotes(added)].sort((a, b) => a.startBeat - b.startBeat || a.midi - b.midi);
}

export function deleteEditableNote(notes: MidiNote[], noteIndex: number): MidiNote[] {
  return cloneNotes(notes.filter((_, index) => index !== noteIndex));
}

export function nudgeEditableNotes(notes: MidiNote[], requestedDeltaBeats: number, target: TimeNudgeTarget): TimeNudgeResult {
  const selected = notes.map((note, index) => ({ note, index })).filter(({ note, index }) => {
    if (target.selectedNoteIndex !== null) return index === target.selectedNoteIndex;
    if (!target.barRange) return false;
    const barBeats = Math.max(1 / 32, target.beatsPerBar);
    const startBeat = Math.min(target.barRange.startBar, target.barRange.endBar) * barBeats;
    const endBeat = (Math.max(target.barRange.startBar, target.barRange.endBar) + 1) * barBeats;
    return note.startBeat >= startBeat && note.startBeat < endBeat;
  });
  if (!selected.length || !Number.isFinite(requestedDeltaBeats) || requestedDeltaBeats === 0) {
    return { notes: cloneNotes(notes), changedCount: 0, appliedDeltaBeats: 0 };
  }
  const earliestStart = Math.min(...selected.map(({ note }) => note.startBeat));
  const appliedDeltaBeats = Math.max(-earliestStart, requestedDeltaBeats);
  if (Math.abs(appliedDeltaBeats) < 1e-9) return { notes: cloneNotes(notes), changedCount: 0, appliedDeltaBeats: 0 };
  const indexes = new Set(selected.map(({ index }) => index));
  return {
    notes: notes.map((note, index) => indexes.has(index) ? { ...note, startBeat: note.startBeat + appliedDeltaBeats } : { ...note }),
    changedCount: selected.length,
    appliedDeltaBeats,
  };
}

export function recordMidiEdit(before: MidiNote[], after: MidiNote[], history: MidiEditHistory, limit = 40) {
  return {
    notes: cloneNotes(after),
    history: { undo: [...history.undo.slice(-(limit - 1)), cloneNotes(before)], redo: [] as MidiNote[][] },
  };
}

export function undoMidiEdit(current: MidiNote[], history: MidiEditHistory, limit = 40) {
  const previous = history.undo.at(-1);
  if (!previous) return null;
  return {
    notes: cloneNotes(previous),
    history: { undo: history.undo.slice(0, -1), redo: [...history.redo.slice(-(limit - 1)), cloneNotes(current)] },
  };
}

export function redoMidiEdit(current: MidiNote[], history: MidiEditHistory, limit = 40) {
  const next = history.redo.at(-1);
  if (!next) return null;
  return {
    notes: cloneNotes(next),
    history: { undo: [...history.undo.slice(-(limit - 1)), cloneNotes(current)], redo: history.redo.slice(0, -1) },
  };
}

export function beatsPerBar(signature: TimeSignature = DEFAULT_TIME_SIGNATURE): number {
  return signature.numerator > 0 && signature.denominator > 0 ? signature.numerator * (4 / signature.denominator) : 4;
}

export function previousBarStart(currentBeat: number, signature: TimeSignature = DEFAULT_TIME_SIGNATURE): number {
  const barBeats = beatsPerBar(signature);
  return Math.max(0, (Math.floor(Math.max(0, currentBeat) / barBeats) - 1) * barBeats);
}

export function playbackStartBeat(playheadBeat: number | null, endBeat: number, earliestBeat = 0): number {
  const safeEarliest = Math.min(0, earliestBeat);
  const current = Math.max(safeEarliest, playheadBeat ?? safeEarliest);
  return current >= Math.max(safeEarliest, endBeat) - 0.0001 ? safeEarliest : current;
}
